import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchEmailTemplates,
  fetchEmailTemplate,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  renderEmailTemplate,
  fetchTemplatePlaceholders,
  EmailTemplate,
  EmailTemplateInput,
  RenderTemplatePayload,
  RenderTemplateResponse,
} from '@/services/api/emailTemplates';
import { toast } from '@/hooks/use-toast';
import { requestDeduplication } from '@/services/requestDeduplication';

const QUERY_KEY = 'emailTemplates';

export const useEmailTemplates = (options?: { category?: string; search?: string; active?: boolean }) => {
  const queryClient = useQueryClient();
  const { category, search, active } = options || {};

  const {
    data: templates = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY, category, search, active],
    queryFn: () => fetchEmailTemplates(category, search, active),
  });

  const createMutation = useMutation({
    mutationFn: createEmailTemplate,
    onSuccess: () => {
      // Drop cached GETs so the list refetch hits the server, not the 90s dedup cache
      requestDeduplication.clearByUrl('/email/templates');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Template created',
        description: 'Your email template has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create template',
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmailTemplateInput> }) =>
      updateEmailTemplate(id, data),
    onSuccess: () => {
      // Drop cached GETs so the list refetch hits the server, not the 90s dedup cache
      requestDeduplication.clearByUrl('/email/templates');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Template updated',
        description: 'Your email template has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update template',
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmailTemplate,
    onSuccess: () => {
      // Drop cached GETs so the list refetch hits the server, not the 90s dedup cache
      requestDeduplication.clearByUrl('/email/templates');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({
        title: 'Template deleted',
        description: 'Your email template has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete template',
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  return {
    templates,
    isLoading,
    error,
    refetch,
    createTemplate: createMutation.mutateAsync,
    updateTemplate: updateMutation.mutateAsync,
    deleteTemplate: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export const useEmailTemplate = (id: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => fetchEmailTemplate(id!),
    enabled: id !== null && id !== '',
  });
};

export const useRenderEmailTemplate = () => {
  const renderMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RenderTemplatePayload }) =>
      renderEmailTemplate(id, payload),
    onError: (error: Error) => {
      toast({
        title: 'Failed to render template',
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  return {
    renderTemplate: renderMutation.mutateAsync,
    isRendering: renderMutation.isPending,
    renderedData: renderMutation.data as RenderTemplateResponse | undefined,
  };
};

export const useTemplatePlaceholders = () => {
  const { data: placeholders = [], isLoading } = useQuery({
    queryKey: [QUERY_KEY, 'placeholders'],
    queryFn: fetchTemplatePlaceholders,
    staleTime: 30 * 60 * 1000,
  });

  return { placeholders, isLoadingPlaceholders: isLoading };
};
